import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Play, Pause, Video, Sliders, RefreshCw, Maximize2 } from 'lucide-react';

export interface VideoMotionCanvasProps {
  imageUrl: string;
  title: string;
  caption?: string;
  accentColor?: string;
  shaderTheme?: string;
  audioMood?: string;
  isCharacter?: boolean;
  onExpand?: () => void;
  className?: string;
}

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  alpha: number;
  color: string;
  pulsePhase: number;
}

export const VideoMotionCanvas: React.FC<VideoMotionCanvasProps> = ({
  imageUrl,
  title,
  caption,
  accentColor = '#00f3ff',
  shaderTheme = 'planck_shear',
  isCharacter = false,
  onExpand,
  className = '',
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const animFrameRef = useRef<number | null>(null);
  const imageObjRef = useRef<HTMLImageElement | null>(null);

  const [isPlaying, setIsPlaying] = useState<boolean>(true);
  const [motionIntensity, setMotionIntensity] = useState<number>(0.85);
  const [showControls, setShowControls] = useState<boolean>(false);
  const [cinematicMode, setCinematicMode] = useState<'60fps' | '24fps'>('60fps');
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [videoTelemetryStatus, setVideoTelemetryStatus] = useState<string>('VEO-2 // MOTION VECTORS LOCKED');

  const particlesRef = useRef<Particle[]>([]);
  const lastTimeRef = useRef<number>(0);
  const frameIntervalRef = useRef<number>(1000 / 60);

  // Initialize atmospheric zero-g particle system
  const initParticles = useCallback((w: number, h: number) => {
    const count = isCharacter ? 24 : 45;
    const parts: Particle[] = [];
    const colors = [accentColor, '#ffffff', '#00f3ff', '#ff9100', '#d500f9'];

    for (let i = 0; i < count; i++) {
      parts.push({
        x: Math.random() * w,
        y: Math.random() * h,
        vx: (Math.random() - 0.45) * 0.4,
        vy: -0.15 - Math.random() * 0.35,
        size: 0.8 + Math.random() * 2.2,
        alpha: 0.15 + Math.random() * 0.6,
        color: colors[Math.floor(Math.random() * colors.length)],
        pulsePhase: Math.random() * Math.PI * 2,
      });
    }
    particlesRef.current = parts;
  }, [accentColor, isCharacter]);

  // Load Source Image
  useEffect(() => {
    setIsLoaded(false);
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = imageUrl;
    img.onload = () => {
      imageObjRef.current = img;
      setIsLoaded(true);
      if (canvasRef.current) {
        initParticles(canvasRef.current.width, canvasRef.current.height);
      }
    };
    img.onerror = () => {
      console.warn(`[VideoMotionCanvas] Failed to load ${imageUrl}, fallback to procedural canvas`);
      setIsLoaded(true);
    };

    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [imageUrl, initParticles]);

  // Handle Cinematic Frame Rate
  useEffect(() => {
    frameIntervalRef.current = cinematicMode === '24fps' ? 1000 / 24 : 1000 / 60;
  }, [cinematicMode]);

  // Main 60fps/24fps Generative Video Motion Render Loop
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let time = 0;

    const render = (timestamp: number) => {
      if (!isPlaying) {
        animFrameRef.current = requestAnimationFrame(render);
        return;
      }

      const elapsed = timestamp - lastTimeRef.current;
      if (elapsed < frameIntervalRef.current) {
        animFrameRef.current = requestAnimationFrame(render);
        return;
      }
      lastTimeRef.current = timestamp - (elapsed % frameIntervalRef.current);

      time += 0.016;

      const w = canvas.width;
      const h = canvas.height;
      if (w === 0 || h === 0) {
        animFrameRef.current = requestAnimationFrame(render);
        return;
      }

      ctx.clearRect(0, 0, w, h);

      const img = imageObjRef.current;
      if (img && img.complete && img.naturalWidth > 0) {
        // 1. 3D Volumetric Camera Drift & Parallax
        const panX = Math.sin(time * 0.32) * 16 * motionIntensity;
        const panY = Math.cos(time * 0.24) * 9 * motionIntensity;
        const zoom = 1.05 + Math.sin(time * 0.18) * 0.038 * motionIntensity;

        // Character breathing micro-motion
        const breathOffsetY = isCharacter ? Math.sin(time * 1.4) * 2.8 * motionIntensity : 0;

        ctx.save();
        ctx.translate(w / 2, h / 2);
        ctx.scale(zoom, zoom);
        ctx.translate(-w / 2 + panX, -h / 2 + panY + breathOffsetY);

        // Aspect-ratio cover drawing
        const scaleCover = Math.max(w / img.naturalWidth, h / img.naturalHeight);
        const dw = img.naturalWidth * scaleCover;
        const dh = img.naturalHeight * scaleCover;
        const dx = (w - dw) / 2;
        const dy = (h - dh) / 2;

        ctx.drawImage(img, dx, dy, dw, dh);
        ctx.restore();

        // 2. Volumetric Light Beam Drift (Anamorphic Starship Beacon)
        ctx.save();
        const lightSweepX = ((Math.sin(time * 0.4) + 1) / 2) * (w * 1.5) - w * 0.25;
        const beamGrad = ctx.createLinearGradient(lightSweepX - 120, 0, lightSweepX + 120, h);
        beamGrad.addColorStop(0, 'rgba(0,0,0,0)');
        beamGrad.addColorStop(0.5, `${accentColor}16`);
        beamGrad.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.fillStyle = beamGrad;
        ctx.fillRect(0, 0, w, h);
        ctx.restore();

        // 3. Planck Scale Grid Distortion & Shimmer (when Planck/Freeze shader)
        if (shaderTheme === 'planck_shear' || shaderTheme === 'temporal_freeze') {
          const scanY = (time * 120) % h;
          ctx.fillStyle = `${accentColor}0a`;
          ctx.fillRect(0, scanY, w, 2);

          // Subtle chromatic aberration border pulse
          const rgbOffset = Math.sin(time * 2.5) * 1.5 * motionIntensity;
          if (rgbOffset > 0.8) {
            ctx.fillStyle = 'rgba(255, 23, 68, 0.04)';
            ctx.fillRect(rgbOffset, 0, w, h);
          }
        }

        // 4. Zero-G Dust / Spark Micro-Particles
        const parts = particlesRef.current;
        for (let i = 0; i < parts.length; i++) {
          const p = parts[i];
          p.x += p.vx;
          p.y += p.vy;
          if (p.y < -10) p.y = h + 10;
          if (p.x < -10) p.x = w + 10;
          if (p.x > w + 10) p.x = -10;

          const pAlpha = p.alpha * (0.6 + 0.4 * Math.sin(time * 2 + p.pulsePhase));
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
          ctx.fillStyle = p.color;
          ctx.globalAlpha = pAlpha;
          ctx.fill();
        }
        ctx.globalAlpha = 1.0;

        // 5. Cinematic Vignette & Edge Feathering
        const vignette = ctx.createRadialGradient(w / 2, h / 2, Math.min(w, h) * 0.38, w / 2, h / 2, Math.max(w, h) * 0.72);
        vignette.addColorStop(0, 'rgba(0, 0, 0, 0)');
        vignette.addColorStop(1, 'rgba(3, 5, 8, 0.82)');
        ctx.fillStyle = vignette;
        ctx.fillRect(0, 0, w, h);
      } else {
        // Fallback loading grid
        ctx.fillStyle = '#060a10';
        ctx.fillRect(0, 0, w, h);
        ctx.strokeStyle = `${accentColor}33`;
        ctx.strokeRect(20, 20, w - 40, h - 40);
      }

      animFrameRef.current = requestAnimationFrame(render);
    };

    animFrameRef.current = requestAnimationFrame(render);

    return () => {
      if (animFrameRef.current) {
        cancelAnimationFrame(animFrameRef.current);
      }
    };
  }, [isPlaying, motionIntensity, cinematicMode, shaderTheme, accentColor, isCharacter]);

  // Adjust canvas resolution dynamically on window resize
  useEffect(() => {
    const handleResize = () => {
      const c = canvasRef.current;
      if (!c) return;
      const rect = c.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      c.width = Math.floor(rect.width * dpr);
      c.height = Math.floor(rect.height * dpr);
      initParticles(c.width, c.height);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [initParticles]);

  const handleTriggerReSynthesis = () => {
    setVideoTelemetryStatus('RE-SYNTHESIZING VECTORS...');
    setTimeout(() => {
      setVideoTelemetryStatus('VEO-2 // OPTICAL FLOW OPTIMIZED');
    }, 900);
  };

  return (
    <div
      className={`video-motion-container ${className}`}
      data-testid="video-motion-canvas"
      onMouseEnter={() => setShowControls(true)}
      onMouseLeave={() => setShowControls(false)}
    >
      {/* 60fps Generative Motion Canvas */}
      <canvas
        ref={canvasRef}
        className="video-motion-canvas-element"
        style={{ opacity: isLoaded ? 1 : 0.4, transition: 'opacity 0.3s ease' }}
      />

      {/* Top Left Video Telemetry Badge */}
      <div className="video-telemetry-pill">
        <span className={`telemetry-recording-dot ${isPlaying ? 'dot-pulsing' : ''}`} />
        <span className="telemetry-badge-text">{videoTelemetryStatus}</span>
        <span className="telemetry-fps-tag">{cinematicMode.toUpperCase()}</span>
      </div>

      {/* Top Right Quick Actions */}
      {onExpand && (
        <button
          onClick={onExpand}
          className="video-expand-btn"
          title="Expand Cinema Mode"
          aria-label="Expand Cinema Mode"
        >
          <Maximize2 size={13} />
        </button>
      )}

      {/* Hover Video Motion Controls Bar */}
      <div className={`video-motion-overlay-bar ${showControls ? 'bar-visible' : ''}`}>
        <div className="video-motion-controls-inner">
          <button
            onClick={() => setIsPlaying(!isPlaying)}
            className="video-ctrl-btn"
            title={isPlaying ? 'Pause Video Motion' : 'Play Video Motion'}
          >
            {isPlaying ? <Pause size={13} fill="currentColor" /> : <Play size={13} fill="currentColor" />}
            <span>{isPlaying ? 'PAUSE' : 'PLAY'}</span>
          </button>

          <button
            onClick={() => setCinematicMode(cinematicMode === '60fps' ? '24fps' : '60fps')}
            className={`video-ctrl-btn ${cinematicMode === '24fps' ? 'ctrl-active' : ''}`}
            title="Toggle 24fps Cinematic Shutter / 60fps Fluid Motion"
          >
            <Video size={13} />
            <span>{cinematicMode === '24fps' ? '24 FPS CINEMA' : '60 FPS FLUID'}</span>
          </button>

          <div className="video-slider-group" title="Adjust 3D Parallax & Motion Depth">
            <Sliders size={12} className="slider-icon" />
            <span className="slider-label">DEPTH: {Math.round(motionIntensity * 100)}%</span>
            <input
              type="range"
              min="0.2"
              max="1.8"
              step="0.05"
              value={motionIntensity}
              onChange={(e) => setMotionIntensity(parseFloat(e.target.value))}
              className="video-depth-slider"
            />
          </div>

          <button
            onClick={handleTriggerReSynthesis}
            className="video-ctrl-btn"
            title="Recalculate AI Optical Flow Vectors"
          >
            <RefreshCw size={12} />
            <span>AI VECTORS</span>
          </button>
        </div>
      </div>

      {/* Bottom Scene Title & Motion Caption */}
      <div className="video-bottom-caption-strip">
        <span className="video-caption-title">{title}</span>
        {caption && <span className="video-caption-sub"> // {caption}</span>}
      </div>
    </div>
  );
};
